'use client'

import { forwardRef, useCallback, useImperativeHandle, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ImageIcon, PlusCircleIcon, SaveIcon, TagIcon, XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Separator } from '@/components/ui/separator'
import { CreateBrandParams, BrandType, UpdateBrandParams } from '@/types/brand'
import { setValidationErrors } from '@/lib/utils'
import { useScrollToError, useUnsavedChangesGuard } from '@/hooks'
import { FormModeEnum } from '@/enums'
import { FileType } from '@/types/file'
import { FileSelector, UnsavedChangesGuardDialog } from '@/components/partials'

type PropsType = {
  initialData?: BrandType | null
  onSubmit: (data: CreateBrandParams | UpdateBrandParams) => void
  onSubmitAndNew?: (data: CreateBrandParams | UpdateBrandParams) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export type BrandFormRef = {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

const BrandForm = forwardRef<BrandFormRef, PropsType>(
  ({ initialData, onSubmit, onSubmitAndNew, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()
    const { scrollToFirstError } = useScrollToError()

    // State for picture
    const [picture, setPicture] = useState<FileType | null>(initialData?.picture ?? null)

    const brandSchema = useCallback(
      () =>
        z.object({
          pictureId: z.string().optional(),
          name: z.string().min(1, t('not_blank'))
        }),
      [t]
    )

    type FormData = z.infer<ReturnType<typeof brandSchema>>

    const form = useForm<FormData>({
      resolver: zodResolver(brandSchema()),
      defaultValues: {
        name: initialData?.name || ''
      }
    })

    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        setValidationErrors(error, form.setError)
        scrollToFirstError()
      },
      reset: () => form.reset()
    }))

    const formErrors = form.formState.errors

    const handlePictureChange = (files: FileType | FileType[] | null) => {
      if (files === null) {
        setPicture(null)
        form.setValue('pictureId', '', { shouldValidate: true })
      } else if ('length' in files) {
        setPicture(files[0])
        form.setValue('pictureId', files[0]?.id || '', { shouldValidate: true })
      } else {
        setPicture(files)
        form.setValue('pictureId', files?.id || '', { shouldValidate: true })
      }
    }

    const handleSubmit = (data: FormData) => {
      const submissionData: CreateBrandParams = {
        name: data.name
      }

      // In CREATE mode: don't send pictureId if no picture selected
      // In UPDATE mode: send empty string if no picture selected
      if (mode === FormModeEnum.CREATE) {
        if (picture?.id) {
          submissionData.pictureId = picture.id
        }
        // If no picture, don't add pictureId at all (undefined)
      } else {
        // UPDATE mode: always send pictureId, empty string if no picture
        submissionData.pictureId = picture?.id || ''
      }

      onSubmit(submissionData)
    }

    const handleSubmitAndNew = (data: FormData) => {
      const submissionData: CreateBrandParams = {
        name: data.name
      }

      // In CREATE mode: don't send pictureId if no picture selected
      // In UPDATE mode: send empty string if no picture selected
      if (mode === FormModeEnum.CREATE) {
        if (picture?.id) {
          submissionData.pictureId = picture.id
        }
        // If no picture, don't add pictureId at all (undefined)
      } else {
        // UPDATE mode: always send pictureId, empty string if no picture
        submissionData.pictureId = picture?.id || ''
      }

      onSubmitAndNew?.(submissionData)
    }

    const isCreateModeWithSubmitAndNew = mode === FormModeEnum.CREATE && !!onSubmitAndNew

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TagIcon className="h-5 w-5" />
            {mode === FormModeEnum.CREATE ? t('create_brand') : t('edit_brand')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(
                isCreateModeWithSubmitAndNew ? handleSubmitAndNew : handleSubmit,
                scrollToFirstError
              )}
              className="space-y-6">
              <div className="grid grid-cols-1">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('name')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('name')} disabled={isLoading} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              {/* Picture */}
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name={'pictureId'}
                  render={({ field }) => <input type="hidden" {...field} value={field.value || ''} />}
                />
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  <h3 className="font-semibold">{t('picture')}</h3>
                </div>
                <div>
                  <FileSelector
                    multiple={false}
                    onlyImages={true}
                    optimizeImageSelection={true}
                    value={picture}
                    onChange={handlePictureChange}
                    buttonLabel={t('select_picture')}
                    buttonLabelChange={t('change_picture')}
                    noSelectionText={t('no_file_selected')}
                    allowMainSelection={true}
                    initialMainId={initialData?.picture?.id}
                  />
                  {formErrors.pictureId && (
                    <p className="text-destructive mt-2 text-sm">
                      {String(formErrors.pictureId.message || t('required'))}
                    </p>
                  )}
                </div>
              </div>

              <Separator />

              <div className="flex items-center justify-between gap-3 pt-1">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                  <XIcon />
                  <span className="hidden sm:inline">{t('cancel')}</span>
                </Button>
                <div className="flex items-center gap-2">
                  {isCreateModeWithSubmitAndNew && (
                    <Button
                      type="button"
                      onClick={() => form.handleSubmit(handleSubmit, scrollToFirstError)()}
                      disabled={isLoading}>
                      <SaveIcon />
                      <span className="hidden sm:inline">{isLoading ? t('saving') : t('save')}</span>
                    </Button>
                  )}
                  <Button type="submit" disabled={isLoading}>
                    {isCreateModeWithSubmitAndNew ? <PlusCircleIcon /> : <SaveIcon />}
                    <span className="hidden sm:inline">
                      {isLoading ? t('saving') : isCreateModeWithSubmitAndNew ? t('save_and_add_new') : t(mode)}
                    </span>
                  </Button>
                </div>
              </div>
              <UnsavedChangesGuardDialog
                open={isNavigationBlocked}
                onConfirm={confirmNavigation}
                onCancel={cancelNavigation}
              />
            </form>
          </Form>
        </CardContent>
      </Card>
    )
  }
)

BrandForm.displayName = 'BrandForm'

export default BrandForm
